import { CardHeaderEditContext } from '@context/card-header-edit-2';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Inline, RadioButtonGroup, RadioButtonGroupItem, Text, Tooltip } from '@ui-lib';
import { useCallback, useContext, useMemo } from 'react';

import { VALUE_PROVIDER_TOUR_CONTAINER_ID } from '../CardItemEditor/utils';
import type { ValueProvider } from '.cache/__types__';

type CardValueProviderSelectorProps = {
  adminOptionTitle?: string | undefined;
  memberOptionTitle?: string | undefined;
  selected: ValueProvider | undefined;
  onChange: (selectedValue: ValueProvider) => void;
};

export const CardValueProviderSelector: React.FC<CardValueProviderSelectorProps> = ({
  adminOptionTitle,
  memberOptionTitle,
  selected,
  onChange,
}) => {
  const { showEditPopover } = useContext(CardHeaderEditContext);

  /**
   * This is a workaround for the fact that RadioButtonGroupItem.value is typed as a string.
   */
  const handleChange = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue as ValueProvider);
    },
    [onChange],
  );

  const copyText = useMemo(() => {
    if (showEditPopover?.type === 'logo') {
      return 'company logo';
    } else if (showEditPopover?.type === 'cover') {
      return 'cover photo';
    } else if (showEditPopover?.type === 'profile') {
      return 'profile picture';
    } else return 'value'; // fallback to agnostic wording
  }, [showEditPopover?.type]);

  return (
    <>
      <Box display="flex" flexDirection="column" gap="medium" marginBottom="small">
        <Box
          id={VALUE_PROVIDER_TOUR_CONTAINER_ID}
          display="flex"
          flexDirection="column"
          gap="medium"
          width="fitContent"
        >
          <Inline alignY="center" gap="xsmall">
            <Text size="medium" weight="demibold">
              Who will provide this value?
            </Text>
            <Tooltip
              label="Fill it if you’re creating a card for your own use, or leave it for a team member to fill in later."
              position="right"
              disabled={false}
              padding="compact"
            >
              <FontAwesomeIcon icon={faExclamationCircle} />
            </Tooltip>
          </Inline>
          <RadioButtonGroup name="example-radio-group" value={selected} onChange={handleChange}>
            <RadioButtonGroupItem value={'admin' as ValueProvider}>
              {adminOptionTitle ?? 'Me'}
            </RadioButtonGroupItem>
            <RadioButtonGroupItem value={'member' as ValueProvider}>
              {memberOptionTitle && memberOptionTitle?.length > 0
                ? memberOptionTitle
                : 'Card owner'}
            </RadioButtonGroupItem>
          </RadioButtonGroup>
        </Box>

        {selected === 'member' && (
          <Box>
            <Text size="small" tone="secondary">
              {memberOptionTitle ?? 'The card owner'} will be prompted to provide a {copyText} when
              claiming this card
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
};
